"use client"
import { EllipsisVertical, Loader2, Plus } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useState } from "react";
import { getContactStatusColor } from "@/utils/common";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { TagLoaders } from "../TagLoaders";
import { Switch } from "../ui/switch";
import { apiFetch } from "@/lib/api/apiFetch";
import { useToast } from "@/hooks/use-toast";
import clsx from "clsx";
import { EMAIL_STATUS } from "@/utils/email-status";

interface ContactsProps {
  contacts: any[]
  loading: boolean
  togglePageCheck: any
  pages: any[]
  pagination: any
  setShowCreateContactDialog: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedContacts: React.Dispatch<React.SetStateAction<any[]>>
  selectedContacts: any[]
  setSelectedContact: any
  setContactToArchive: React.Dispatch<React.SetStateAction<Contact | null>>
  setShowArchiveDialog: React.Dispatch<React.SetStateAction<boolean>>
  setContactToEdit: React.Dispatch<React.SetStateAction<Contact | null>>
  setShowEditContactDialog: React.Dispatch<React.SetStateAction<boolean>>
  refreshContacts: () => Promise<void>
  listsFilter: string
}

interface Contact {
  id: string
  email: string
  name: string
  source: string
  location?: string
  status: any
  remarks?: string
  createdAt: string
  updatedAt: string
  isActive: boolean
  [key: string]: any
}

const ContactTable = ({
  contacts,
  loading,
  togglePageCheck,
  pages,
  pagination,
  setShowCreateContactDialog,
  setSelectedContacts,
  selectedContacts,
  setSelectedContact,
  setContactToArchive,
  setShowArchiveDialog,
  setContactToEdit,
  setShowEditContactDialog,
  refreshContacts,
  listsFilter
}: ContactsProps) => {

  const [openOptionsId, setOpenOptionsId] = useState("");
  const [archivingContacts, setArchivingContacts] = useState<Set<string>>(new Set())
  const [togglingStatusIds, setTogglingStatusIds] = useState<Set<string>>(new Set())
  const [updatingEmailStatusId, setUpdatingEmailStatusId] = useState<string | null>(null)
  const { toast } = useToast()

  const handleSelectContact = (contactId: string, checked: boolean) => {
    if (checked) {
      const contact = contacts.find(c => c?.id === contactId)
      if (contact) {
        setSelectedContacts([...selectedContacts, contact])
      }
    } else {
      setSelectedContacts(selectedContacts.filter((c) => c?.id !== contactId))
    }
  }

  const handleEditContact = (contact: Contact) => {
    setContactToEdit(contact)
    setShowEditContactDialog(true)
    setOpenOptionsId("")
  }

  const handleArchiveContact = async (contactId: string) => {
    setContactToArchive(contacts.find(c => c.id === contactId) || null)
    setShowArchiveDialog(true)
    setOpenOptionsId("")
  }

  /**
   * Toggles the active status of a contact.
   * 
   * @param contactId - The unique identifier of the contact.
   * @param currentStatus - The current active status of the contact.
   */

  const handleUpdateEmailStatus = async (contactId: string, newStatus: string) => {
    try {
      setUpdatingEmailStatusId(contactId)

      const response = await apiFetch(`/api/contacts/${contactId}/email-status`, {
        method: "PUT",
        data: {
          emailStatus: newStatus
        }
      })

      if (response && !response.error) {
        await refreshContacts()
        toast({
          title: "Email status updated",
          description: `Email status is now ${newStatus.replace("_", " ")}.`
        })
      }
    } catch (error: any) {
      toast({
        title: "Error updating email status",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })
    } finally {
      setUpdatingEmailStatusId(null)
    }
  }

  const handleToggleStatus = async (contactId: string, currentStatus: boolean) => {
    try {
      setTogglingStatusIds(prev => new Set(prev).add(contactId))

      const response = await apiFetch("/api/contacts", {
        method: "PUT",
        data: {
          id: contactId,
          isActive: !currentStatus
        }
      })

      if (response && !response.error) {
        await refreshContacts()
        toast({
          title: "Status updated",
          description: `Contact is now ${!currentStatus ? "active" : "disabled"}.`
        })
      }
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })
    } finally {
      setTogglingStatusIds(prev => {
        const next = new Set(prev)
        next.delete(contactId)
        return next
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getFirstList = (lists: any[], listsFilter?: string) => {
    if (listsFilter && listsFilter) {
      const id = listsFilter?.includes("_") ? listsFilter.split("_")[1] : listsFilter

      return lists.find((l: any) => l?.id === id)
    }
    return lists[0]
  }

  const statusClasses: Record<string, string> = {
    safe: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    unsafe: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    bounced:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    unsubscribed:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    invalid:
      "bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300",
    risky:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    verification_failed:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    unverified:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  };

  const handleEnable = async (contact: any) => {
    try {
      const response = await apiFetch(`/api/contacts`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          ids: [contact.id],
          action: "enable",
        },
      })

      if (response && !response.error) {
        if (response.code === "CONTACTS_ENABLED") {
          await refreshContacts()
          toast({
            title: "Contact Enabled",
            description: `Contact is now enabled.`
          })
        }
        else {
          toast({
            title: "Contact Not Enabled",
            description: `Contact is not enabled.`,
            variant: "destructive"
          })
        }
      }
    } catch (error: any) {
      toast({
        title: "Error updating status",
        description: error.message || "Something went wrong",
        variant: "destructive"
      })
    }

  }

  return (
    <Table className="text-xs [&_td]:py-1.5 [&_td]:px-2 [&_th]:py-2 [&_th]:px-2 [&_th]:h-9">

      {/* HEADER */}
      <TableHeader className="bg-gray-50 dark:bg-gray-800">
        <TableRow>
          <TableHead className="w-12">
            <Checkbox
              className="scale-90"
              checked={pages.find(p => p.page === pagination.page)?.isAllChecked || false}
              onCheckedChange={() => { togglePageCheck(pagination.page, contacts) }}
            />
          </TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Phone</TableHead>
          <TableHead>Email Status</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Company</TableHead>
          <TableHead>Lists</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-8 text-center pt-2">Operation</TableHead>
        </TableRow>
      </TableHeader>

      {/* BODY */}
      <TableBody className="divide-y divide-gray-200 dark:divide-gray-800">

        {loading ? (
          <TableRow>
            <TableCell colSpan={11} className="text-center py-8 text-gray-600 dark:text-gray-300">
              <TagLoaders
                tags={["Contacts"]}
                message="Fetching contact records..."
                variant="pulse"
              />
            </TableCell>
          </TableRow>

        ) : contacts.length === 0 ? (
          <TableRow>
            <TableCell colSpan={13} className="text-center py-8 h-full">
              <div className="text-gray-500 dark:text-gray-400">
                <p className="mb-2">No contacts found</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateContactDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first contact
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          contacts.map((contact) => (
            <TableRow
              key={contact.id}
              onClick={() => setSelectedContact(contact)}
              className={clsx(
                "transition-colors cursor-pointer",
                contact.isActive === false
                  ? "bg-red-50 dark:bg-red-900/40"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >

              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  className="scale-90"
                  checked={selectedContacts.some((c) => c?.id === contact.id)}
                  disabled={contact.isActive === false}
                  onCheckedChange={(checked) =>
                    handleSelectContact(contact.id, checked as boolean)
                  }
                />
              </TableCell>
              <TableCell
                className={clsx(
                  "font-medium hover:text-blue-600 dark:hover:text-blue-400",
                  "max-w-[150px] md:max-w-[200px] lg:max-w-[250px] break-all whitespace-normal",
                  !contact.isActive &&
                  "text-gray-400 dark:text-gray-500 line-through opacity-90"
                )}
              >
                <div className="line-clamp-2" title={contact.email}>
                  {contact.email}
                </div>
              </TableCell>

              <TableCell className="text-gray-700 dark:text-gray-300">
                <div className="line-clamp-2" title={contact.name}>
                  {contact.name}
                </div>
              </TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300">
                <div className="line-clamp-2" title={contact.phone}>
                  {contact.phone || "--"}
                </div>
              </TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    asChild
                    disabled={updatingEmailStatusId === contact.id || !contact.isActive}
                  >
                    <div
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full capitalize transition-opacity cursor-pointer hover:opacity-80 ${(EMAIL_STATUS as any)[contact.emailStatus]?.badge || "bg-gray-100 text-gray-500"}`}
                      title={"Click to update status"}
                    >
                      {updatingEmailStatusId === contact.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mx-auto" />
                      ) : (
                        contact.emailStatus
                          ? contact.emailStatus.replace("_", " ")
                          : "--"
                      )}
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl z-50">
                    {["safe", "unsafe", "risky", "bounced", "unsubscribed", "invalid", "verification_failed", "unverified"].map((status) => (
                      <DropdownMenuItem
                        key={status}
                        onClick={() => handleUpdateEmailStatus(contact.id, status)}
                        className={clsx(
                          "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 capitalize py-2",
                          contact.emailStatus === status && "bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-medium"
                        )}
                      >
                        {status.replace("_", " ")}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>

              <TableCell className="text-gray-700 dark:text-gray-300">
                <div className="line-clamp-2" title={contact.source?.name}>
                  {contact.source?.name || "--"}
                </div>
              </TableCell>

              <TableCell className="text-gray-700 dark:text-gray-300">
                <div className="line-clamp-2" title={contact.company}>
                  {contact.company || "--"}
                </div>
              </TableCell>
              <TableCell className="max-w-[120px]">
                {(!contact.lists || contact.lists.length === 0) ? (
                  <span className="text-gray-400 dark:text-gray-500">--</span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="secondary"
                      className="px-2 py-0.5 h-5 text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-none max-w-[90px] truncate"
                      title={getFirstList(contact.lists, listsFilter)?.name}
                    >
                      {getFirstList(contact.lists, listsFilter)?.name}
                    </Badge>
                    {contact.lists.length > 1 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Badge
                            variant="outline"
                            className="px-1.5 py-0.5 h-5 text-[10px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 font-bold"
                            onClick={(e) => e.stopPropagation()}
                          >
                            +{contact.lists.length - 1}
                          </Badge>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-48 p-2 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-xl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="space-y-1">
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 mb-1">Enrolled Lists</h4>
                            <div className="max-h-40 overflow-y-auto custom-scrollbar">
                                {contact.lists.map((list: any) => (
                                  <div
                                    key={list?.id}
                                    className="text-[14px] px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-300 transition-colors truncate"
                                    title={list?.name}
                                  >
                                    {list?.name}
                                  </div>
                                ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                )}
              </TableCell>

              <TableCell className="text-gray-700 dark:text-gray-300">
                {(() => {
                  const loc = [contact.city, contact.state].filter(Boolean).join(", ") || contact.location || "—-";
                  return (
                    <div className="line-clamp-2" title={loc}>
                      {loc}
                    </div>
                  );
                })()}
              </TableCell>

              <TableCell>
                {contact.status?.name ?
                  <Badge
                    style={getContactStatusColor(contact?.status?.colorCode)}
                    className="status-badge-custom text-[10px] px-1.5 py-0 h-5 leading-none font-semibold transition-colors duration-150"
                  >
                    {contact.status?.name}
                  </Badge>
                  :
                  <span>--</span>
                }
              </TableCell>
              {/* <TableCell>
                <div className="max-w-xs text-gray-700 dark:text-gray-300">
                  {contact.remarks ? (
                    <div className="relative">
                      <p className="text-xs line-clamp-2 md:line-clamp-1">
                        {contact.remarks}
                      </p>
                      {contact.remarks.length > 30 && (
                        <span
                          onClick={() => setSelectedContact(contact)}
                          className="text-blue-600 dark:text-blue-400 text-[10px] cursor-pointer hover:underline"
                        >
                          Show more
                        </span>
                      )}
                    </div>
                  ) : (
                    "--"
                  )}
                </div>
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
              </TableCell> */}
              {/* <TableCell>
                <div className="flex items-center gap-2">
                  {togglingStatusIds.has(contact.id) ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <Switch
                      className="scale-[0.8]"
                      checked={contact.isActive !== false}
                      onCheckedChange={() => handleToggleStatus(contact.id, contact.isActive !== false)}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell className="relative" onClick={(e) => e.stopPropagation()}>
              </TableCell> */}
              <TableCell className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div
                      role="button"
                      tabIndex={0}
                      className="p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenOptionsId(openOptionsId === contact.id ? "" : contact.id);
                      }}
                    >
                      <EllipsisVertical
                        size={14}
                        strokeWidth={2}
                        className="mx-auto cursor-pointer text-gray-700 dark:text-gray-300"
                      />
                    </div>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    onClick={(e) => e.stopPropagation()}
                    className="w-36 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg rounded-md"
                  >
                    {
                      contact.isActive ? (
                        <DropdownMenuItem
                          onClick={() => handleEditContact(contact)}
                          className="hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Edit
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => handleEnable(contact)}
                          className="hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Enable
                        </DropdownMenuItem>
                      )
                    }

                    <DropdownMenuItem
                      onClick={() => handleArchiveContact(contact.id)}
                      className="hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                    >
                      {archivingContacts.has(contact.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Archive"
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

              </TableCell>

            </TableRow>
          ))
        )}

      </TableBody>
    </Table>
  );
}

export default ContactTable;
